/**
 * Batch B4: per-agent usage breakdown for Admin → Billing & Usage.
 *
 * `GET /api/billing/usage` returns `data.usage.by_agent` — a per-agent
 * roll-up of the current billing cycle's `api_calls` rows
 * (`[{agent_id, agent_name, api_calls, tokens}, ...]`, backend-sorted by
 * api_calls desc). This is the single tested unit that normalizes + formats
 * those rows for the BillingTab render shell:
 *   - defends against a missing / non-array payload (older API, error path),
 *   - coerces counts to numbers and re-sorts by api_calls desc (so the UI
 *     never depends on server ordering),
 *   - falls back the display name when the agent was deleted since its calls
 *     were logged,
 *   - pre-formats the compact token label the cards render.
 *
 * Keeping this out of the JSX keeps the render wiring a thin host shell.
 */

/**
 * Compact token count label, e.g. 1500 → "1.5K", 900 → "900".
 * @param {number|string|null|undefined} tokens
 * @returns {string}
 */
export function formatTokens(tokens) {
  const n = Number(tokens);
  const safe = Number.isFinite(n) ? n : 0;
  if (safe >= 1000) return `${(safe / 1000).toFixed(1)}K`;
  return `${safe}`;
}

/**
 * Normalize + sort the `by_agent` usage array for rendering.
 * @param {Array<{agent_id?: string, agent_name?: string, api_calls?: number, tokens?: number}>|null|undefined} byAgent
 * @returns {Array<{agentId: string|null, agentName: string, apiCalls: number, tokens: number, tokensLabel: string}>}
 */
export function summarizeAgentUsage(byAgent) {
  if (!Array.isArray(byAgent)) return [];
  const rows = byAgent.map((a) => {
    const apiCallsRaw = Number(a && a.api_calls);
    const tokensRaw = Number(a && a.tokens);
    const apiCalls = Number.isFinite(apiCallsRaw) ? apiCallsRaw : 0;
    const tokens = Number.isFinite(tokensRaw) ? tokensRaw : 0;
    const agentId = a && a.agent_id != null ? a.agent_id : null;
    const agentName = (a && a.agent_name) || "Unknown agent";
    return { agentId, agentName, apiCalls, tokens, tokensLabel: formatTokens(tokens) };
  });
  rows.sort((x, y) => y.apiCalls - x.apiCalls);
  return rows;
}
